import {
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  MinusIcon,
  StarIcon,
  TrophyIcon
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';

interface LeaderboardCardProps {
  rank: number;
  name: string;
  department: string;
  score: number;
  change?: number;
  isCurrentUser?: boolean;
}

const LeaderboardCard = ({ 
  rank, 
  name, 
  department, 
  score, 
  change = 0,
  isCurrentUser = false,
}: LeaderboardCardProps) => {
  const getRankIcon = () => {
    if (rank === 1) return <TrophyIcon className="w-5 h-5" />;
    if (rank === 2) return <StarIcon className="w-5 h-5" />;
    if (rank === 3) return <StarIcon className="w-5 h-5" />;
    return null;
  };

  const getRankColors = () => {
    if (rank === 1) return "bg-gradient-to-r from-yellow-400 to-yellow-600 text-white shadow-lg";
    if (rank === 2) return "bg-gradient-to-r from-gray-300 to-gray-500 text-white shadow-lg";
    if (rank === 3) return "bg-gradient-to-r from-amber-600 to-amber-800 text-white shadow-lg";
    return "bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 text-gray-700 dark:text-gray-300";
  };

  const getChangeIcon = () => {
    if (change > 0) return <ArrowTrendingUpIcon className="w-4 h-4" />;
    if (change < 0) return <ArrowTrendingDownIcon className="w-4 h-4" />;
    return <MinusIcon className="w-4 h-4" />;
  };

  const getChangeColor = () => {
    if (change > 0) return "text-green-600 dark:text-green-400";
    if (change < 0) return "text-red-600 dark:text-red-400";
    return "text-gray-500 dark:text-gray-400";
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.05 * rank }}
      whileHover={{ 
        scale: 1.02, 
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" 
      }}
      className={`relative group rounded-xl border p-4 transition-all duration-300 ${
        isCurrentUser 
          ? 'border-primary-200 bg-gradient-to-r from-primary-50 to-primary-100 dark:border-primary-900 dark:from-primary-900/20 dark:to-primary-800/20 shadow-lg' 
          : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 shadow-md hover:shadow-lg'
      }`}
    >
      {/* Current User Indicator */}
      {isCurrentUser && (
        <div className="absolute -top-2 -right-2 w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
          <span className="text-white text-xs font-bold">YOU</span>
        </div>
      )}

      <div className="flex items-center space-x-4">
        {/* Rank Badge */}
        <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${getRankColors()} relative`}>
          {getRankIcon() && (
            <div className="absolute -top-1 -right-1">
              {getRankIcon()}
            </div>
          )}
          <span className="text-lg font-bold">{rank}</span>
        </div>
        
        {/* User Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            <h3 className={`text-lg font-semibold truncate ${
              isCurrentUser 
                ? 'text-primary-900 dark:text-primary-100' 
                : 'text-gray-900 dark:text-white'
            }`}>
              {name}
            </h3>
            {isCurrentUser && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                You
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center space-x-1">
            <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
            <span>{department}</span>
          </p>
        </div>
        
        {/* Score and Change */}
        <div className="text-right">
          <div className="flex items-center space-x-2">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {score.toLocaleString()}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">pts</div>
          </div>
          
          <div className={`flex items-center justify-end space-x-1 text-sm font-medium ${getChangeColor()}`}>
            {getChangeIcon()}
            <span>
              {change > 0 && '+'}
              {change}
              {change !== 0 ? ' pts' : '—'}
            </span>
          </div>
        </div>
      </div>

      {/* Hover Effect */}
      <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
    </motion.div>
  );
};

export default LeaderboardCard;
